












































































































































































import {
	computed,
	defineComponent,
	nextTick,
	Ref,
	ref,
} from '@vue/composition-api';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { inviteGroupMembers } from '@front-app/services/GroupsApi/GroupsMemberApi';
import {
	idsToColleagueInvites,
	emailsToExternalInvites,
	getColleagueEmails,
} from '@front-app/modules/group/util/invite';
import { ButtonPalettes } from '@front-app/components/buttons/palettes';
import { Size } from '@front-app/components/constants';
import { v } from '@front-app/components/form/validation/useValidation';
import { atLeastOneIsRequired } from '@front-app/components/form/validation/validators';
import { SelectBoxChoice } from '@front-app/components/inputs/SelectBoxModel';
import { useFocusFirstInvalidField } from '@front-app/hooks/useFocusFirstInvalidField';
import { useUsername } from '@front-app/hooks/useTranslate';
import { INVITE_BY, MemberTypeOption } from '@front-app/modules/group/components/admin/addMembers/addMembers';
import InviteRoleMax from '@front-app/modules/groupMember/components/InviteRoleMax.vue';
import { useMemberConditions } from '@front-app/modules/groupMember/hooks/useMemberConditions';
import { UserRole } from '@front-app/services/UserApi/UserModel';
import { ColleaguesStore } from '@front-app/store/singletons/Colleagues';
import { UserAvatarStore, UserStore } from '@front-app/store/singletons/User';
import { isNonValidEmailAddress } from '@front-app/util/validate';
import { useAsyncStatus } from '@front-app/hooks/useAsyncStatus';
import CtaButton from '@front-app/components/buttons/CtaButton.vue';
import RemoveButton from '@front-app/components/buttons/concrete/XButton.vue';
import EmailListInput from '@front-app/components/form/EmailListInput.vue';
import FormError from '@front-app/components/form/FormError.vue';
import FormLabel from '@front-app/components/form/FormLabel.vue';
import FormSection from '@front-app/components/form/FormSection.vue';
import SearchColleagues from '@front-app/components/inputs/SearchColleagues.vue';
import SelectBox from '@front-app/components/inputs/SelectBox.vue';
import SimpleCard from '@front-app/components/cards/SimpleCard.vue';
import UserAvatar from '@front-app/components/user/avatar.vue';
import ErrorCard from '@front-app/components/cards/state/ErrorCard.vue';
import ConfirmationModal from '@front-app/components/modals/ConfirmationModal.vue';
import { useConfirmDialog } from '@vueuse/core';

const INVITE_BY_OPTIONS = [
	INVITE_BY.EMAIL_ADDRESS,
	INVITE_BY.TIMEDRIGHT_CONTACTS,
];

const INVITE_BY_OPTION_LABELS: Record<INVITE_BY, string> = {
	[INVITE_BY.EMAIL_ADDRESS]: 'email-address',
	[INVITE_BY.TIMEDRIGHT_CONTACTS]: 'group-admin.add-members.timedright-contacts',
};

const MEMBER_TYPE_OPTIONS: MemberTypeOption[] = [
	UserRole.MEMBER,
	UserRole.ADMINISTRATOR,
];

const MEMBER_TYPE_OPTION_LABELS: Record<MemberTypeOption, string> = {
	[UserRole.MEMBER]: 'role_badge.member-regular',
	[UserRole.ADMINISTRATOR]: 'role_badge.administrator',
	[UserRole.NONE]: 'none',
};

export default defineComponent({
	components: {
		CtaButton,
		EmailListInput,
		FormError,
		FormLabel,
		FormSection,
		InviteRoleMax,
		RemoveButton,
		SearchColleagues,
		SelectBox,
		SimpleCard,
		UserAvatar,
		FontAwesomeIcon,
		ErrorCard,
		ConfirmationModal,
	},
	props: {
		groupId: {
			type: String,
			required: true,
		},
	},
	setup(props, ctx) {
		// Email addresses
		const emailAddressText = ref('');
		const emailAddressList = computed(() => emailAddressText.value
			.split(',')
			.map((val) => val.trim())
			.filter(Boolean));
		const badEmailAddresses = computed(() => emailAddressList.value.filter(isNonValidEmailAddress));
		// END Email addresses

		// TimedRight contacts
		const getUsername = useUsername(ctx);
		const { colleagues } = ColleaguesStore;
		const selectedColleagueIds: Ref<string[]> = ref([]);
		const selectedColleagues = computed(() => selectedColleagueIds.value.map((id) => UserStore.getInstance().getFullUser(id).value));
		const removeFromInviteesList = (toRemove: string) => {
			selectedColleagueIds.value = selectedColleagueIds.value.filter((id) => id !== toRemove);
		};
		// END TimedRight contacts

		// Add by
		const addByOptions: Ref<SelectBoxChoice[]> = computed(() => INVITE_BY_OPTIONS
			.map((option) => ({
				key: option,
				label: ctx.root.$t(INVITE_BY_OPTION_LABELS[option]) as unknown as string,
			})));
		const selectedAddByOption = ref(INVITE_BY.EMAIL_ADDRESS);
		const selectedAddByOptionLabel = computed(() => ctx.root.$t(INVITE_BY_OPTION_LABELS[selectedAddByOption.value]) as unknown as string);
		const selectInviteBy = (option: INVITE_BY) => {
			selectedAddByOption.value = option;

			// We need to clear the values to ensure that we cannot switch types and then move forward with an empty value
			emailAddressText.value = '';
			selectedColleagueIds.value = [];
		};
		// END Add by

		// Validation
		const validation = v({
			invitees: {
				value: selectedColleagueIds,
				rules: [atLeastOneIsRequired(() => emailAddressList.value)],
			},
		});

		const { focusFirstInvalid } = useFocusFirstInvalidField({ containerSelector: '#invite-form' });
		// END Validation

		// Member types
		const memberTypes: Ref<SelectBoxChoice[]> = computed(() => MEMBER_TYPE_OPTIONS
			.map((option) => ({
				key: option,
				label: ctx.root.$t(MEMBER_TYPE_OPTION_LABELS[option]) as unknown as string,
			})));
		const selectedMemberType: Ref<MemberTypeOption> = ref(UserRole.MEMBER);
		const selectedMemberTypeLabel = computed(() => ctx.root.$t(MEMBER_TYPE_OPTION_LABELS[selectedMemberType.value]));
		const selectMemberType = (option: MemberTypeOption) => {
			selectedMemberType.value = option;
		};
		const { hasReachedMaxForRole } = useMemberConditions(computed(() => props.groupId), selectedMemberType);

		// END Member types

		// Next step
		const addMemberStatus = useAsyncStatus();
		const adminRoleConfirmation = useConfirmDialog();
		const memberRoleConfirmation = useConfirmDialog();
		const grantAccessAndContinue = async () => {
			validation.validate();
			if (!validation.isValid.value) {
				await nextTick();
				focusFirstInvalid();
				return;
			}

			if (selectedMemberType.value === UserRole.ADMINISTRATOR) {
				const { isCanceled } = await adminRoleConfirmation.reveal();
				if (isCanceled) {
					return;
				}
			} else {
				const { isCanceled } = await memberRoleConfirmation.reveal();
				if (isCanceled) {
					return;
				}
			}

			await addMemberStatus.track(inviteGroupMembers(props.groupId, {
				subject: '',
				message: '',
				inviteInternal: false,
				inviteExternal: false,
				emailExternal: false,
				colleagues: idsToColleagueInvites(selectedAddByOption.value === INVITE_BY.TIMEDRIGHT_CONTACTS ? selectedColleagueIds.value : [], selectedMemberType.value),
				external: emailsToExternalInvites(selectedAddByOption.value === INVITE_BY.EMAIL_ADDRESS ? emailAddressList.value : [], selectedMemberType.value),
			}));

			ctx.emit('continue', {
				type: selectedAddByOption.value,
				emailsList: selectedAddByOption.value === INVITE_BY.EMAIL_ADDRESS ? emailAddressText.value : getColleagueEmails(selectedColleagues.value),
				members: selectedAddByOption.value === INVITE_BY.EMAIL_ADDRESS ? emailAddressList.value : selectedColleagueIds.value,
				role: selectedMemberType.value,
			});
		};

		const continueWithoutGrantingAccess = () => {
			ctx.emit('continue', {
				type: selectedAddByOption.value,
				emailsList: '',
				members: [],
				role: UserRole.NONE,
			});
		};
		// END Next step

		return {
			validation,
			grantAccessAndContinue,
			continueWithoutGrantingAccess,
			addMemberStatus,
			// Confirmation modals
			adminRoleConfirmation,
			memberRoleConfirmation,
			// Add by
			addByOptions,
			selectedAddByOption,
			selectedAddByOptionLabel,
			selectInviteBy,
			INVITE_BY,
			// Email addresses
			emailAddressText,
			badEmailAddresses,
			// TimedRight contacts
			colleagues,
			selectedColleagueIds,
			selectedColleagues,
			removeFromInviteesList,
			getUsername,
			// Member types
			memberTypes,
			selectedMemberType,
			selectedMemberTypeLabel,
			selectMemberType,
			hasReachedMaxForRole,
			// Avatar
			getAvatar: UserAvatarStore.get,
			fetchAvatar: UserAvatarStore.fetch,
			// Util
			ButtonPalettes,
			Size,
		};
	},
});
